import { devLog, devError } from '../utils/devLogger';
/**
 * Admin Document Upload Component
 *
 * Allows admins and facilitators to upload documents on behalf of learners
 * with comprehensive search, validation, and audit capabilities.
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Table,
  Badge,
  Modal,
  ProgressBar,
  InputGroup,
  Spinner,
  Accordion
} from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { DOCUMENT_CATEGORIES } from '../config/documentCategories';
import adminDocumentUploadService from '../services/adminDocumentUploadService';
import { updateAdminDocumentsToVerified, checkAdminDocumentStatus } from '../utils/updateAdminDocuments';
import '../styles/brand.css';

const AdminDocumentUpload = () => {
  const { userProfile } = useAuth();

  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedLearner, setSelectedLearner] = useState(null);
  const [learnerDetails, setLearnerDetails] = useState(null);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(false);

  // Upload state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('CV');
  const [uploadReason, setUploadReason] = useState('');
  const [uploadNotes, setUploadNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Bulk upload state
  const [showBulkModal, setShowBulkModal] = useState(false);

  // Document status fix state
  const [fixingDocuments, setFixingDocuments] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [documentStats, setDocumentStats] = useState(null);

  // Messages
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Access control
  const [hasPermission, setHasPermission] = useState(false);

  useEffect(() => {
    checkPermissions();
  }, [userProfile]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkPermissions = async () => {
    devLog('🔒 AdminDocumentUpload: Checking permissions for user:', userProfile);

    if (userProfile?.role === 'admin' || userProfile?.role === 'facilitator' || userProfile?.role === 'qa') {
      devLog('✅ AdminDocumentUpload: User has permissions');
      setHasPermission(true);
      try {
        await adminDocumentUploadService.initialize();
        devLog('✅ AdminDocumentUpload: Service initialized successfully');
      } catch (error) {
        devError('❌ AdminDocumentUpload: Service initialization failed:', error);
        setError('Failed to initialize document upload service: ' + error.message);
      }
    } else {
      devLog('❌ AdminDocumentUpload: User lacks permissions. Role:', userProfile?.role);
      setHasPermission(false);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim() || searchTerm.length < 2) {
      setError('Please enter at least 2 characters to search');
      return;
    }

    setSearching(true);
    setError('');
    devLog('🔍 AdminDocumentUpload: Starting search for:', searchTerm);

    try {
      const results = await adminDocumentUploadService.searchLearners(searchTerm);
      devLog('📋 AdminDocumentUpload: Search results:', results);
      setSearchResults(results);

      if (results.length === 0) {
        setError('No learners found matching your search criteria. Try searching with partial names, email addresses, or ID numbers.');
        devLog('❌ No results found for search term:', searchTerm);
      } else {
        devLog('✅ Found', results.length, 'learners');
      }
    } catch (error) {
      devError('❌ AdminDocumentUpload search error:', error);
      setError('Search failed: ' + error.message);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleSelectLearner = async (learner) => {
    setSelectedLearner(learner);
    setLoading(true);
    setError('');

    try {
      const details = await adminDocumentUploadService.getLearnerDetails(learner.id);
      setLearnerDetails(details);
    } catch (error) {
      setError('Failed to load learner details: ' + error.message);
      setLearnerDetails(null);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setError('');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    if (!uploadReason.trim()) {
      setError('Please provide a reason for this upload');
      return;
    }

    setUploading(true);
    setError('');
    setUploadProgress(0);

    try {
      const result = await adminDocumentUploadService.uploadDocumentForLearner(
        selectedLearner.id,
        selectedFile,
        selectedCategory,
        {
          reason: uploadReason,
          notes: uploadNotes
        }
      );

      setSuccess(result.message);
      setShowUploadModal(false);

      // Reset form
      setSelectedFile(null);
      setUploadReason('');
      setUploadNotes('');

      // Refresh learner details
      const updatedDetails = await adminDocumentUploadService.getLearnerDetails(selectedLearner.id);
      setLearnerDetails(updatedDetails);

      // Dispatch custom event to notify other components
      window.dispatchEvent(new CustomEvent('documentUploaded', {
        detail: {
          learnerId: selectedLearner.id,
          category: selectedCategory,
          adminUpload: true
        }
      }));

    } catch (error) {
      setError('Upload failed: ' + error.message);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const getDocumentStatusBadge = (category) => {
    const document = learnerDetails?.documents[category];

    if (!document) {
      return <Badge bg="warning">Missing</Badge>;
    }

    switch (document.status) {
      case 'verified':
        return <Badge bg="success">Verified</Badge>;
      case 'pending_verification':
        return <Badge bg="info">Pending</Badge>;
      case 'rejected':
        return <Badge bg="danger">Rejected</Badge>;
      default:
        return <Badge bg="secondary">Unknown</Badge>;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';

    try {
      // Handle Firestore Timestamp
      if (date.toDate && typeof date.toDate === 'function') {
        const jsDate = date.toDate();
        if (!isNaN(jsDate.getTime())) {
          return jsDate.toLocaleString('en-ZA', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          });
        }
      }

      // Handle JavaScript Date object
      if (date instanceof Date) {
        if (!isNaN(date.getTime())) {
          return date.toLocaleString('en-ZA', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          });
        }
      }

      // Handle timestamp number (milliseconds)
      if (typeof date === 'number') {
        const jsDate = new Date(date);
        if (!isNaN(jsDate.getTime())) {
          return jsDate.toLocaleString('en-ZA', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          });
        }
      }

      // Handle Firestore Timestamp object with seconds/nanoseconds
      if (date.seconds || date._seconds) {
        const seconds = date.seconds || date._seconds;
        const jsDate = new Date(seconds * 1000);
        if (!isNaN(jsDate.getTime())) {
          return jsDate.toLocaleString('en-ZA', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          });
        }
      }

      // Try parsing as string
      const parsedDate = new Date(date);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate.toLocaleString('en-ZA', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      }

      return 'N/A';
    } catch (error) {
      devLog('Error formatting date:', date, error);
      return 'N/A';
    }
  };

  const handleCheckDocumentStatus = async () => {
    setCheckingStatus(true);
    setError('');
    try {
      const stats = await checkAdminDocumentStatus();
      setDocumentStats(stats);
      setSuccess(`Document status check complete. Found ${stats.total} admin uploads: ${stats.verified} verified, ${stats.pending} pending.`);
    } catch (error) {
      setError('Failed to check document status: ' + error.message);
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleFixDocumentStatus = async () => {
    setFixingDocuments(true);
    setError('');
    try {
      const result = await updateAdminDocumentsToVerified();
      if (result.success) {
        setSuccess(`Successfully updated ${result.totalUpdated} documents to verified status across ${result.usersAffected} users.`);
        // Refresh document stats
        await handleCheckDocumentStatus();
      } else {
        setError('Failed to update documents: ' + result.error);
      }
    } catch (error) {
      setError('Failed to fix document status: ' + error.message);
    } finally {
      setFixingDocuments(false);
    }
  };

  if (!hasPermission) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <h4>Access Denied</h4>
          <p>You do not have permission to upload documents for other users.</p>
          <p>This feature is restricted to administrators and facilitators.</p>
        </Alert>
      </Container>
    );
  }

  return (
    <>
      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row>
            <Col lg={8}>
              <h1 className="mb-3">Admin Document Upload</h1>
              <p className="mission-text mb-4">
                Upload documents on behalf of learners with full audit trail for POPIA compliance
              </p>
              <p className="mb-0">
                Securely upload and manage learner documents with comprehensive search,
                validation, and audit capabilities for regulatory compliance and data protection.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      <Container className="mt-4">

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Document Status Fix Section */}
      {userProfile?.role === 'admin' && (
        <Card className="mb-4">
          <Card.Header>
            <h5>Document Status Management</h5>
          </Card.Header>
          <Card.Body>
            <p className="mb-3">
              Use these tools to check and fix document status issues. This will update existing admin-uploaded
              documents from "pending" to "verified" status.
            </p>

            <Row>
              <Col md={6}>
                <Button
                  variant="outline-primary"
                  onClick={handleCheckDocumentStatus}
                  disabled={checkingStatus}
                  className="me-2"
                >
                  {checkingStatus ? <Spinner size="sm" className="me-2" /> : ''}
                  Check Document Status
                </Button>
              </Col>
              <Col md={6}>
                <Button
                  variant="warning"
                  onClick={handleFixDocumentStatus}
                  disabled={fixingDocuments || !documentStats}
                  className="me-2"
                >
                  {fixingDocuments ? <Spinner size="sm" className="me-2" /> : ''}
                  Fix Pending Admin Uploads
                </Button>
              </Col>
            </Row>

            {documentStats && (
              <Alert variant="info" className="mt-3">
                <strong>Current Status:</strong> {documentStats.total} total admin uploads found -
                {documentStats.verified} verified, {documentStats.pending} pending, {documentStats.other} other
              </Alert>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Search Section */}
      <Card className="mb-4">
        <Card.Header>
          <h5>Search Learners</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={8}>
              <InputGroup className="mb-3">
                <Form.Control
                  type="text"
                  placeholder="Search by ID number, email, or name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button
                  variant="primary"
                  onClick={handleSearch}
                  disabled={searching || searchTerm.length < 2}
                >
                  {searching ? <Spinner size="sm" /> : 'Search'}
                </Button>
              </InputGroup>
            </Col>
            <Col md={4}>
              <Button
                variant="outline-secondary"
                onClick={() => setShowBulkModal(true)}
                className="w-100"
              >
                Bulk Upload
              </Button>
            </Col>
          </Row>

          {searchResults.length > 0 && (
            <Table striped bordered hover responsive className="mt-3">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>ID Number</th>
                  <th>Province</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {searchResults.map((learner) => (
                  <tr key={learner.id}>
                    <td>{learner.displayName}</td>
                    <td>{learner.email}</td>
                    <td>{learner.idNumber}</td>
                    <td>{learner.province}</td>
                    <td>
                      <Badge bg={learner.status === 'active' ? 'success' : 'secondary'}>
                        {learner.status}
                      </Badge>
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => handleSelectLearner(learner)}
                      >
                        Select
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Selected Learner Details */}
      {selectedLearner && (
        <Card className="mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h5>Learner Details: {selectedLearner.displayName}</h5>
            <Button
              variant="success"
              onClick={() => setShowUploadModal(true)}
              disabled={loading || !learnerDetails}
            >
              Upload Document
            </Button>
          </Card.Header>
          <Card.Body>
            {loading ? (
              <div className="text-center py-4">
                <Spinner animation="border" />
                <p className="mt-2">Loading learner details...</p>
              </div>
            ) : learnerDetails ? (
              <>
                <Row className="mb-3">
                  <Col md={3}>
                    <strong>Email:</strong><br />
                    {learnerDetails.email}
                  </Col>
                  <Col md={3}>
                    <strong>ID Number:</strong><br />
                    {learnerDetails.idNumber}
                  </Col>
                  <Col md={3}>
                    <strong>Province:</strong><br />
                    {learnerDetails.province}
                  </Col>
                  <Col md={3}>
                    <strong>Registration:</strong><br />
                    {formatDate(learnerDetails.registrationDate)}
                  </Col>
                </Row>

                <Accordion>
                  <Accordion.Item eventKey="0">
                    <Accordion.Header>Document Status Overview</Accordion.Header>
                    <Accordion.Body>
                      <Table striped bordered hover responsive>
                        <thead>
                          <tr>
                            <th>Document Category</th>
                            <th>Status</th>
                            <th>Uploaded Date</th>
                            <th>File Name</th>
                            <th>Uploaded By</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(DOCUMENT_CATEGORIES).map(([key, category]) => {
                            const document = learnerDetails.documents[key];
                            return (
                              <tr key={key}>
                                <td>
                                  <strong>{category.name}</strong>
                                  {category.mandatory && <Badge bg="info" className="ms-2">Required</Badge>}
                                </td>
                                <td>{getDocumentStatusBadge(key)}</td>
                                <td>{document ? formatDate(document.uploadDate) : 'N/A'}</td>
                                <td>{document ? document.fileName : 'N/A'}</td>
                                <td>
                                  {document?.adminUpload ? (
                                    <Badge bg="warning">Admin Upload</Badge>
                                  ) : document ? (
                                    'Self Upload'
                                  ) : (
                                    'N/A'
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              </>
            ) : null}
          </Card.Body>
        </Card>
      )}

      {/* Upload Modal */}
      <Modal show={showUploadModal} onHide={() => setShowUploadModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Upload Document for {selectedLearner?.displayName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Document Category *</Form.Label>
                  <Form.Select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    {Object.entries(DOCUMENT_CATEGORIES).map(([key, category]) => (
                      <option key={key} value={key}>
                        {category.name} {category.mandatory && '(Required)'}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Select File *</Form.Label>
                  <Form.Control
                    type="file"
                    onChange={handleFileSelect}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  />
                  {selectedFile && (
                    <Form.Text className="text-muted">
                      Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Reason for Upload *</Form.Label>
              <Form.Select
                value={uploadReason}
                onChange={(e) => setUploadReason(e.target.value)}
              >
                <option value="">Select reason...</option>
                <option value="Student technical difficulties">Student technical difficulties</option>
                <option value="Facilitator assistance requested">Facilitator assistance requested</option>
                <option value="Document submitted via email">Document submitted via email</option>
                <option value="Document submitted in person">Document submitted in person</option>
                <option value="System backup/recovery">System backup/recovery</option>
                <option value="Compliance requirement">Compliance requirement</option>
                <option value="Other administrative reason">Other administrative reason</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Additional Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={uploadNotes}
                onChange={(e) => setUploadNotes(e.target.value)}
                placeholder="Any additional information about this upload..."
              />
            </Form.Group>

            {uploading && (
              <div className="mb-3">
                <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} />
              </div>
            )}

            <Alert variant="info">
              <small>
                <strong>POPIA Compliance Notice:</strong> This upload will be logged in the audit trail
                with your user details, timestamp, and reason for compliance purposes.
              </small>
            </Alert>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUploadModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpload}
            disabled={!selectedFile || !uploadReason.trim() || uploading}
          >
            {uploading ? 'Uploading...' : 'Upload Document'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Bulk Upload Modal */}
      <Modal show={showBulkModal} onHide={() => setShowBulkModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Bulk Document Upload</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <strong>Bulk Upload Feature:</strong> This feature allows you to upload multiple documents
            for multiple learners simultaneously. Each upload will be individually logged for audit purposes.
          </Alert>
          <p className="text-muted">
            Implementation note: Full bulk upload interface to be completed in next phase.
            For now, please use individual uploads above.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBulkModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
    </>
  );
};

export default AdminDocumentUpload;
